import {
    InteractionElement,
    InteractionElementAttribute,
    InteractionSection,
    InteractionTargetType,
} from '../models';

export class ActivityTrackerUtils {
    private getElementAttributes(elem: HTMLElement): InteractionElementAttribute[] {
        const blacklist: string[] = ['class', 'value', 'id', 'name'];

        return Array.from(elem.attributes)
            .map((i) => ({
                name: i.name,
                value: i.value,
            }))
            .filter((i) => blacklist.indexOf(i.name) === -1);
    }

    private getElementText(elem: HTMLElement) {
        if (elem instanceof HTMLAnchorElement || elem instanceof HTMLButtonElement) {
            return elem.innerText;
        }

        if (
            elem instanceof HTMLInputElement &&
            (elem.type === 'button' || elem.type === 'submit' || elem.type === 'reset')
        ) {
            return elem.value;
        }

        if (elem === document.body) {
            return '<body>';
        }

        if (elem === document.documentElement) {
            return '<document>';
        }

        if (elem === document.head) {
            return '<head>';
        }

        if ((elem as HTMLInputElement).labels?.length) {
            return Array.from((elem as HTMLInputElement).labels || [])
                .map((i) => i.innerText)
                .join(' ');
        }

        return null;
    }

    public getFirstParentElement(node: Node): HTMLElement | null {
        let n: Node | null = node;

        while (n) {
            if (n instanceof HTMLElement) {
                return n;
            }

            n = n.parentElement;
        }

        return null;
    }

    public getTargetType(target: Node): InteractionTargetType {
        let type: InteractionTargetType = 'other';

        if (target.nodeName === 'A') {
            type = 'link';
        } else if (
            target.nodeName === 'BUTTON' ||
            (target instanceof HTMLInputElement && target.type === 'button')
        ) {
            type = 'button';
        } else if (target instanceof HTMLInputElement) {
            if (target.type === 'submit') {
                type = 'submit';
            } else if (target.type === 'reset') {
                type = 'reset';
            } else if (target.type === 'file') {
                type = 'file';
            } else if (
                target.type !== 'submit' &&
                target.type !== 'reset' &&
                target.type !== 'file'
            ) {
                type = 'input';
            }
        }

        return type;
    }

    public hasAttribute(attribute: string, element?: HTMLElement) {
        return element?.hasAttribute(attribute) || element?.hasAttribute(`data-${attribute}`);
    }

    public clickableTarget(element: HTMLElement) {
        let nodeElement: HTMLElement | null = element;
        let isClickable = false;
        while (nodeElement && !isClickable) {
            const elementType = this.getTargetType(nodeElement);
            isClickable = Boolean(
                elementType === 'submit' ||
                    elementType === 'button' ||
                    elementType === 'link' ||
                    this.hasAttribute('pulse-clickable', nodeElement),
            );
            if (!isClickable) {
                nodeElement = nodeElement.parentElement;
            }
        }

        return nodeElement;
    }

    public createInteractionElement(elem: HTMLElement): InteractionElement {
        const nodeType = this.getTargetType(elem);
        const classList = Array.from(elem.classList);
        const attributes = this.getElementAttributes(elem);

        const name =
            elem.getAttribute('pulse-name') ||
            elem.getAttribute('data-pulse-name') ||
            elem.getAttribute('pulse-clickable') ||
            elem.getAttribute('data-pulse-clickable') ||
            (elem as HTMLInputElement).name ||
            elem.getAttribute('name') ||
            undefined;

        return {
            attributes,
            attributeValueByName: attributes.reduce(
                (acc, attr) => ({ ...acc, [attr.name]: attr.value }),
                {},
            ),
            classList,
            nodeType,
            id: elem.id,
            name,
            nodeName: elem.nodeName,
            text: this.getElementText(elem) || undefined,
        };
    }

    public createSection(
        sectionElement: HTMLElement | undefined,
        sectionGroupElement: HTMLElement | undefined,
        popupComponentElement: HTMLElement | undefined = undefined,
        dynamicAttributes?: Record<string, string>,
    ): InteractionSection {
        const section = sectionElement
            ? sectionElement.getAttribute('pulse-section') ||
              sectionElement.getAttribute('data-pulse-section')
            : null;

        const sectionGroup = sectionGroupElement
            ? sectionGroupElement.getAttribute('pulse-section-group') ||
              sectionGroupElement.getAttribute('data-pulse-section-group')
            : null;

        const popupComponent = popupComponentElement
            ? popupComponentElement.getAttribute('pulse-component') ||
              popupComponentElement.getAttribute('data-pulse-component')
            : null;

        const popupComponentType = popupComponentElement
            ? popupComponentElement.getAttribute('pulse-component-type') ||
              popupComponentElement.getAttribute('data-pulse-component-type')
            : null;

        return {
            name: section || sectionGroup || undefined,
            group: sectionGroup || section || undefined,
            sectionElement: sectionElement && this.createInteractionElement(sectionElement),
            sectionGroupElement:
                sectionGroupElement && this.createInteractionElement(sectionGroupElement),
            popupComponent: popupComponent || undefined,
            popupComponentType: popupComponentType || undefined,
            additionalProperties:
                dynamicAttributes && Object.keys(dynamicAttributes).length > 0
                    ? dynamicAttributes
                    : undefined,
        };
    }

    public collectDynamicAttributes(element: HTMLElement): Record<string, string> {
        const dynamicAttributes: Record<string, string> = {};

        const elementsToProcess: HTMLElement[] = [];
        let currentElement: HTMLElement | null = element;

        while (currentElement && currentElement !== document.documentElement) {
            elementsToProcess.push(currentElement);
            currentElement = currentElement.parentElement;
        }

        for (let i = elementsToProcess.length - 1; i >= 0; i--) {
            const elem = elementsToProcess[i];
            this.collectDynamicAttributesFromElement(elem, dynamicAttributes);
        }

        return dynamicAttributes;
    }

    private collectDynamicAttributesFromElement(
        element: HTMLElement,
        dynamicAttributes: Record<string, string>,
    ): void {
        Array.from(element.attributes).forEach((attr) => {
            const attrName = attr.name;
            const attrValue = attr.value;

            // Check for data-pulse-dynamic-* attributes
            if (attrName.startsWith('data-pulse-dynamic-')) {
                const dynamicKey = attrName.replace('data-pulse-dynamic-', '');
                dynamicAttributes[dynamicKey] = attrValue;
            }
            // Check for pulse-dynamic-* attributes (without data prefix)
            else if (attrName.startsWith('pulse-dynamic-')) {
                const dynamicKey = attrName.replace('pulse-dynamic-', '');
                dynamicAttributes[dynamicKey] = attrValue;
            }
        });
    }
}
